import { useEffect, useState } from "react";
import {
  LayoutDashboard, Package, ShoppingBag, Plus, Pencil, Trash2, IndianRupee, AlertTriangle, Wallet, Store, Clock,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import VendorGate from "@/pages/vendor/VendorGate";
import { VendorOrdersPanel, VendorEarningsPanel, VendorStorePanel } from "@/pages/vendor/VendorPanels";
import { Pill } from "@/components/dash";
import api, { apiError } from "@/lib/api";
import { formatINR } from "@/constants/branding";
import DashboardShell, { StatCard } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "products", label: "My Products", icon: Package },
  { key: "orders", label: "Orders", icon: ShoppingBag },
  { key: "earnings", label: "Earnings & Payouts", icon: Wallet },
  { key: "store", label: "Store Profile", icon: Store },
];

const emptyProduct = { name: "", description: "", price: "", mrp: "", category_slug: "", images: [""], stock: "", active: true };

export default function VendorDashboard() {
  const { user } = useAuth();
  // Sellers only get the full dashboard after a Super Admin approves their application
  if (user.role === "vendor" && user.vendor_status && user.vendor_status !== "approved") return <VendorGate />;
  return <VendorApproved />;
}

function VendorApproved() {
  const [active, setActive] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyProduct);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    const [s, p, o, c] = await Promise.all([
      api.get("/vendor/stats"), api.get("/vendor/products"),
      api.get("/vendor/orders"), api.get("/categories"),
    ]);
    setStats(s.data); setProducts(p.data); setOrders(o.data); setCategories(c.data);
  };

  useEffect(() => { loadAll().catch((e) => toast.error(apiError(e))); }, []);

  const openNew = () => { setForm(emptyProduct); setEditId(null); setDialogOpen(true); };
  const openEdit = (p) => {
    setForm({ ...p, price: String(p.price), mrp: String(p.mrp || ""), stock: String(p.stock), images: p.images?.length ? p.images : [""] });
    setEditId(p.id); setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.category_slug) return toast.error("Please select a category.");
    setSaving(true);
    const payload = {
      name: form.name,
      description: form.description,
      price: parseFloat(form.price) || 0,
      mrp: form.mrp ? parseFloat(form.mrp) : null,
      category_slug: form.category_slug,
      images: form.images.filter(Boolean),
      stock: parseInt(form.stock, 10) || 0,
      active: form.active,
    };
    try {
      if (editId) await api.put(`/vendor/products/${editId}`, payload);
      else await api.post("/vendor/products", payload);
      toast.success(editId ? "Product updated" : "Product added");
      setDialogOpen(false);
      loadAll();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/vendor/products/${id}`);
      toast.success("Product deleted");
      loadAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <DashboardShell title="Vendor" subtitle="Vendor Dashboard" nav={NAV} active={active} onNavigate={setActive}>
      {active === "overview" && stats && (
        <div className="space-y-4">
          {(stats.pending_products > 0 || stats.rejected_products > 0) && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex flex-wrap gap-x-6 gap-y-1" data-testid="vendor-review-banner">
              {stats.pending_products > 0 && <span><b>{stats.pending_products}</b> product(s) awaiting Zilkart review — they go live once approved.</span>}
              {stats.rejected_products > 0 && <span><b>{stats.rejected_products}</b> product(s) need changes — open My Products to see why.</span>}
            </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Products" value={stats.products} icon={Package} />
            <StatCard label="Orders" value={stats.orders} icon={ShoppingBag} />
            <StatCard label="Gross sales" value={formatINR(stats.revenue)} icon={IndianRupee} />
            <StatCard label="Low Stock" value={stats.low_stock} icon={AlertTriangle} />
            <StatCard label="Orders to confirm" value={stats.pending_orders} icon={Clock} />
            <StatCard label="Net earnings" value={formatINR(stats.net_earnings)} icon={Wallet} />
            <StatCard label="Available to withdraw" value={formatINR(stats.available_balance)} icon={Wallet} />
            <StatCard label="Commission" value={`${stats.commission_rate}%`} icon={Store} />
          </div>
        </div>
      )}

      {active === "products" && (
        <div>
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-semibold text-lg">My Products ({products.length})</h2>
            <Button className="gap-1.5" onClick={openNew} data-testid="vendor-add-product"><Plus className="h-4 w-4" /> Add Product</Button>
          </div>
          {products.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground bg-white rounded-xl border border-border">
              <Package className="h-10 w-10 mx-auto mb-2" />
              <p className="font-medium">No products yet</p>
              <p className="text-sm">Add your first product to start selling.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-muted-foreground">
                  <tr>
                    <th className="p-3 font-medium">Product</th>
                    <th className="p-3 font-medium">Category</th>
                    <th className="p-3 font-medium">Price</th>
                    <th className="p-3 font-medium">Stock</th>
                    <th className="p-3 font-medium">Status</th>
                    <th className="p-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-t border-border" data-testid={`vendor-product-${p.id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <img src={(p.images || [])[0]} alt="" className="h-9 w-9 rounded object-cover bg-muted" />
                          <span className="font-medium line-clamp-1 max-w-[180px]">{p.name}</span>
                        </div>
                      </td>
                      <td className="p-3 text-muted-foreground">{p.category}</td>
                      <td className="p-3 font-medium">{formatINR(p.price)}</td>
                      <td className="p-3">{p.stock}</td>
                      <td className="p-3">
                        <div className="flex flex-col items-start gap-1">
                          <Pill tone={p.moderation === "rejected" ? "red" : p.moderation === "pending" ? "amber" : p.active ? "green" : "slate"}>
                            {p.moderation === "pending" ? "In review" : p.moderation === "rejected" ? "Rejected" : p.active ? "Live" : "Hidden"}
                          </Pill>
                          {p.moderation === "rejected" && p.moderation_note && <span className="text-xs text-red-700 max-w-[180px]">{p.moderation_note}</span>}
                          {p.vendor_suspended && <span className="text-xs text-red-700">Store suspended</span>}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`vendor-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(p.id)} data-testid={`vendor-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {active === "orders" && <VendorOrdersPanel orders={orders} reload={loadAll} />}
      {active === "earnings" && <VendorEarningsPanel />}
      {active === "store" && <VendorStorePanel />}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="vendor-product-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="product-form-name" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Price (₹)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required data-testid="product-form-price" /></div>
              <div><Label>MRP (₹)</Label><Input type="number" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category_slug} onValueChange={(v) => setForm({ ...form, category_slug: v })}>
                  <SelectTrigger data-testid="product-form-category"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} required data-testid="product-form-stock" /></div>
            </div>
            <div><Label>Image URL</Label><Input value={form.images[0]} onChange={(e) => setForm({ ...form, images: [e.target.value] })} placeholder="https://…" data-testid="product-form-image" /></div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Visible on storefront</label>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="product-form-save">{saving ? "Saving…" : "Save Product"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
